import React, { FC, useMemo } from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts'
import { formatNumberLocale, formatNumber } from '../../../_utils'
import {
  ClicksPerPath,
  ImpressionsPerPath,
  ITransaction
} from './types'

type Props = {
  clicksPerPath: ClicksPerPath[]
  badClicksPerPath: ClicksPerPath[]
  impressionsPerPath: ImpressionsPerPath[]
  transactions: ITransaction[]
  commissionAdjustment?: number
  currency: string
}

const COLORS_PRIMARY = ['#0d6efd', '#e9ecef']
const COLORS_STATUS = ['#198754', '#dc3545']

const Donut: FC<{
  value: number
  rest: number
  centerText: string
  label: string
  colors?: string[]
}> = ({ value, rest, centerText, label, colors = COLORS_PRIMARY }) => {
  const data = [
    { name: 'value', value },
    { name: 'rest', value: Math.max(rest, 0) }
  ]

  return (
    <div className="d-flex flex-column align-items-center">
      <div style={{ width: 120, height: 120, position: 'relative' }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              innerRadius={42}
              outerRadius={55}
              dataKey="value"
              stroke="none"
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: 16
          }}
        >
          {centerText}
        </div>
      </div>

      <small className="text-muted mt-1">{label}</small>
    </div>
  )
}

const KpiCard: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card className="border-0" style={{height:'82px'}}>
    <Card.Body className="d-flex flex-column justify-content-center align-items-center p-2">
      <div className="fs-4 fw-bold">{value}</div>
      <small className="text-muted">{label}</small>
    </Card.Body>
  </Card>
)

const LinkKpiOverview: FC<Props> = ({
  clicksPerPath,
  badClicksPerPath,
  impressionsPerPath,
  transactions,
  commissionAdjustment = 0,
  currency
}) => {
  const kpis = useMemo(() => {
    const trackedClicks = clicksPerPath.reduce(
      (sum, c) => sum + (c.clicks || 0),
      0
    )

    const untrackedClicks = badClicksPerPath.reduce(
      (sum, c) => sum + (c.clicks || 0),
      0
    )

    const impressions = impressionsPerPath.reduce(
      (sum, i) => sum + (i.impressions || 0),
      0
    )

    const transactionCommission = transactions.reduce(
      (sum, t) => sum + (t.totalCommission || 0),
      0
    )
    const commission = transactionCommission + commissionAdjustment

    const totalClicks = trackedClicks + untrackedClicks

    return {
      trackedClicks,
      untrackedClicks,
      totalClicks,
      impressions,
      commission
    }
  }, [clicksPerPath, badClicksPerPath, impressionsPerPath, transactions, commissionAdjustment])

  const trackedClicksPercent =
    kpis.totalClicks > 0
      ? (kpis.trackedClicks / kpis.totalClicks) * 100
      : 0

  const epc =
    kpis.trackedClicks > 0
      ? kpis.commission / kpis.trackedClicks
      : 0

  const epv =
    kpis.impressions > 0
      ? kpis.commission / kpis.impressions
      : 0

  const rpm =
    kpis.impressions > 0
      ? (kpis.commission / kpis.impressions) * 1000
      : 0

  return (
    <Row className="mb-4 g-3">
      {/* Automated clicks – fuld højde */}
      <Col md={3}>
        <Card className="border-0" style={{height:'180px'}}>
          <Card.Body className="d-flex justify-content-center align-items-center">
            <Donut
              value={kpis.trackedClicks}
              rest={kpis.untrackedClicks}
              centerText={`${formatNumberLocale(trackedClicksPercent, 1)}%`}
              label="Automated clicks"
              colors={COLORS_STATUS}
            />
          </Card.Body>
        </Card>
      </Col>

      <Col md={9}>
        <Row className="g-3">
            <Col md={4}>
                <KpiCard
                label="Page views"
                value={formatNumberLocale(kpis.impressions, 0)}
                />
            </Col>
            <Col md={4}>
                <KpiCard
                label="Total clicks"
                value={formatNumberLocale(kpis.totalClicks, 0)}
                />
            </Col>
            <Col md={4}>
                <KpiCard
                label="Commission"
                value={formatNumber(kpis.commission, currency)}
                />
            </Col>

            <Col md={4}>
                <KpiCard
                label="EPC"
                value={formatNumber(epc, currency)}
                />
            </Col>
            <Col md={4}>
                <KpiCard
                label="EPV"
                value={formatNumber(epv, currency)}
                />
            </Col>
            <Col md={4}>
                <KpiCard
                label="RPM"
                value={formatNumber(rpm, currency)}
                />
            </Col>

        </Row>
      </Col>
    </Row>
  )
}

export default LinkKpiOverview
